import React, { useState, useEffect } from 'react';
import { Trash2, Send, Loader2, Info, Bell } from 'lucide-react';
import api from '../services/api';

const MuralScreen = ({ currentUser }) => {
    const [notices, setNotices] = useState([]);
    const [newNotice, setNewNotice] = useState('');
    const [loading, setLoading] = useState(true);

    // Buscar avisos da API
    const loadNotices = async () => {
        try {
            const res = await api.get('/notices');
            setNotices(res.data || []);
        } catch (error) {
            console.error("Erro ao buscar avisos:", error);
        } finally {
            setLoading(false);
        }
    };

    // Carrega ao abrir a tela
    useEffect(() => {
        loadNotices();
    }, []);

    const handleAddNotice = async (e) => {
        e.preventDefault();
        if (!newNotice.trim()) return;

        try {
            await api.post('/notices', { message: newNotice });
            setNewNotice('');
            loadNotices(); // Recarrega a lista
        } catch (error) {
            alert('Erro ao publicar aviso.');
        }
    };

    const handleDeleteNotice = async (id) => {
        if (!window.confirm("Tem certeza que deseja apagar este aviso?")) return;
        try {
            await api.delete(`/notices/${id}`);
            loadNotices();
        } catch (error) {
            alert('Erro ao excluir aviso.');
        }
    };

    const formatDate = (dateString, id) => {
        let d = dateString;
        if (!d && id) {
            try {
                const ts = parseInt(String(id).substring(0, 8), 16) * 1000;
                if (!isNaN(ts)) d = new Date(ts);
            } catch (e) {}
        }
        if (!d) return 'Data desconhecida';
        const parsed = new Date(d);
        if (isNaN(parsed.getTime())) return 'Data desconhecida';
        return parsed.toLocaleString('pt-BR', { 
            day: '2-digit', month: '2-digit', year: '2-digit', 
            hour: '2-digit', minute: '2-digit' 
        });
    };

    const postItColors = ['bg-yellow-200', 'bg-pink-200', 'bg-blue-200', 'bg-green-200', 'bg-orange-200'];

    return (
        <div className="p-4 md:p-8 h-full overflow-y-auto bg-gray-50">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4 md:mb-6 flex items-center">
                <Bell className="w-8 h-8 mr-3 text-blue-600" /> Mural de Avisos
            </h1>
            
            {/* Área de Criação */}
            <div className="bg-white p-4 md:p-6 rounded-lg shadow-md mb-6 md:mb-8 border border-gray-100">
                <form onSubmit={handleAddNotice} className="flex flex-col md:flex-row gap-3">
                    <input 
                        type="text" 
                        placeholder="Escreva um comunicado para a equipe..." 
                        value={newNotice} 
                        onChange={e => setNewNotice(e.target.value)} 
                        className="flex-1 px-4 py-3 md:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
                    />
                    <button 
                        type="submit" 
                        disabled={!newNotice.trim()}
                        className="bg-blue-600 text-white py-3 md:py-2 px-6 rounded-md hover:bg-blue-700 font-medium flex items-center justify-center w-full md:w-auto transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                    >
                        <span className="md:hidden mr-2">Postar</span>
                        <span className="hidden md:inline">Publicar Aviso</span>
                        <Send className="w-4 h-4 ml-2" />
                    </button>
                </form>
            </div>

            {/* Lista de Avisos */}
            {loading ? (
                <div className="text-center py-12 flex flex-col items-center justify-center text-gray-400">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-500 mb-2"/>
                    <p>Carregando avisos...</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 pb-20">
                    {notices.map((notice, index) => {
                        const colorClass = postItColors[index % postItColors.length];
                        const rotateClass = index % 2 === 0 ? (index % 3 === 0 ? '-rotate-2' : 'rotate-1') : (index % 3 === 0 ? 'rotate-2' : '-rotate-1');
                        
                        return (
                            <div key={notice._id || notice.id} className={`${colorClass} p-5 shadow-sm hover:shadow-md hover:scale-105 transition-all outline outline-1 outline-black/5 flex flex-col justify-between relative animate-in fade-in zoom-in h-64 ${rotateClass}`}>
                                {/* Pin/Tape Deco (Opicional) */}
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-8 h-2 bg-white/40 shadow-sm rotate-2"></div>
                                
                                {/* Content */}
                                <div className="flex-1 overflow-y-auto custom-scrollbar mt-3 mb-3 pr-1 text-gray-800 text-sm whitespace-pre-wrap break-words leading-relaxed font-semibold">
                                    {notice.message}
                                </div>
                                
                                {/* Metadata Footer */}
                                <div className="mt-auto text-xs text-gray-700 flex flex-col border-t border-black/10 pt-2 shrink-0">
                                    <span className="font-bold truncate" title={notice.authorName || 'Sistema'}>
                                        {notice.authorName || 'Sistema'}
                                    </span>
                                    <span className="text-gray-600/80 mt-0.5">
                                        {formatDate(notice.createdAt, notice._id || notice.id)}
                                    </span>
                                </div>
                                
                                {/* Botão Excluir (Absolute) */}
                                {(currentUser?.role === 'admin' || currentUser?.name === notice.authorName) && (
                                    <button 
                                        onClick={() => handleDeleteNotice(notice._id || notice.id)} 
                                        className="absolute bottom-2 right-2 text-gray-500 hover:text-red-600 p-1.5 transition-colors rounded hover:bg-white/40"
                                        title="Excluir aviso"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                    
                    {notices.length === 0 && (
                        <div className="col-span-full text-center text-gray-400 py-16 bg-white rounded-lg border border-dashed border-gray-300">
                            <Info className="w-10 h-10 mx-auto mb-3 text-gray-300"/>
                            <p>Nenhum aviso publicado no momento.</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MuralScreen;